import React, { useState, useEffect, useCallback } from 'react';
import './App.css';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.cooltek.xyz';

// Category icons mapping — prefix included (fab for brands, fas for solid)
const categoryIcons = {
  'GitHub Repos': 'fab fa-github',
  'General AI': 'fas fa-robot',
  'Website Builders': 'fas fa-globe',
  'Text-to-Video': 'fas fa-video',
  'Image Generation': 'fas fa-image',
  'Writing & Copy': 'fas fa-pen-fancy',
  'Productivity': 'fas fa-tasks',
  'Audio & Voice': 'fas fa-microphone',
  'Coding': 'fas fa-code',
  'Design': 'fas fa-palette',
  'Marketing': 'fas fa-bullhorn',
  'Research': 'fas fa-flask',
};

const categoryColors = {
  'GitHub Repos': '#238636',
  'General AI': '#6366f1',
  'Website Builders': '#22c55e',
  'Text-to-Video': '#f43f5e',
  'Image Generation': '#ec4899',
  'Writing & Copy': '#14b8a6',
  'Productivity': '#f59e0b',
  'Audio & Voice': '#8b5cf6',
  'Coding': '#3b82f6',
  'Design': '#e11d48',
  'Marketing': '#0ea5e9',
  'Research': '#84cc16',
};

// Helper: resolve icon/color for categories (handles "GitHub - Python" prefix)
function getCategoryIcon(name) {
  if (categoryIcons[name]) return categoryIcons[name];
  if (name && name.startsWith('GitHub')) return 'fab fa-github';
  return 'fas fa-folder';
}

function getCategoryColor(name) {
  if (categoryColors[name]) return categoryColors[name];
  if (name && name.startsWith('GitHub')) return '#238636';
  return '#6366f1';
}

// Auth context
const AuthContext = React.createContext(null);

function useAuth() {
  return React.useContext(AuthContext);
}

// Tool Card Component
function ToolCard({ tool, onDelete, onEditImage, isOwner }) {
  const [imgError, setImgError] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const categoryColor = getCategoryColor(tool.category);
  const displayImage = tool.custom_image_url || tool.og_image_url;
  
  const handleVisit = () => {
    window.open(tool.website_url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="tool-card" data-testid={`tool-card-${tool.id}`}>
      <div className="tool-image-container">
        {displayImage && !imgError ? (
          <img 
            src={displayImage} 
            alt={tool.name}
            className="tool-image"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="tool-image-placeholder" style={{ backgroundColor: categoryColor + '20' }}>
            <i className={getCategoryIcon(tool.category)} style={{ color: categoryColor }}></i>
          </div>
        )}
        {tool.github_stars && (
          <div className="github-stars">
            <i className="fas fa-star"></i> {tool.github_stars.toLocaleString()}
          </div>
        )}
        {isOwner && (
          <div className="tool-menu">
            <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>
              <i className="fas fa-ellipsis-v"></i>
            </button>
            {showMenu && (
              <div className="menu-dropdown">
                <button onClick={() => { onEditImage(tool); setShowMenu(false); }}>
                  <i className="fas fa-image"></i> Change Image
                </button>
                <button className="danger" onClick={() => { onDelete(tool); setShowMenu(false); }}>
                  <i className="fas fa-trash"></i> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      <div className="tool-content">
        <h3 className="tool-name">{tool.name}</h3>
        
        <span className="tool-category" style={{ backgroundColor: categoryColor + '15', color: categoryColor }}>
          <i className={getCategoryIcon(tool.category)}></i>
          {tool.category}
          {tool.github_language && <span className="lang-badge">{tool.github_language}</span>}
        </span>
        
        {tool.description && (
          <p className="tool-description">{tool.description}</p>
        )}
        
        {tool.tags && tool.tags.length > 0 && (
          <div className="tool-tags">
            {tool.tags.slice(0, 4).map((tag, idx) => (
              <span key={idx} className="tool-tag">#{tag}</span>
            ))}
          </div>
        )}
        
        <div className="tool-footer">
          <button className="visit-btn" onClick={handleVisit} data-testid={`visit-btn-${tool.id}`}>
            Visit <i className="fas fa-external-link-alt"></i>
          </button>
        </div>
      </div>
    </div>
  );
}

// Login Modal
function LoginModal({ onClose }) {
  const telegramBotUsername = 'cooltekbot';
  
  const handleGoogleLogin = () => {
    const clientId = '772194946773-q9apq10ncjtcroblfkoa7g4go8mihr5j.apps.googleusercontent.com';
    const redirectUri = encodeURIComponent(`${API_URL}/api/auth/google/callback`);
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&prompt=select_account`;
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content login-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        
        <h2>Login to Cool Tek</h2>
        <p>Access your personal dashboard</p>
        
        <div className="login-options">
          <button className="login-btn google" onClick={handleGoogleLogin}>
            <i className="fab fa-google"></i>
            Continue with Google
          </button>
          
          <div className="divider">
            <span>or</span>
          </div>
          
          <a 
            href={`https://t.me/${telegramBotUsername}?start=login`} 
            target="_blank" 
            rel="noopener noreferrer"
            className="login-btn telegram"
          >
            <i className="fab fa-telegram-plane"></i>
            Login via Telegram Bot
          </a>
          <p className="login-hint">Send /login to @{telegramBotUsername}</p>
        </div>
      </div>
    </div>
  );
}

// Edit Image Modal
function EditImageModal({ tool, onClose, onSave }) {
  const [imageUrl, setImageUrl] = useState(tool.custom_image_url || tool.og_image_url || '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onSave(tool.id, imageUrl);
    setLoading(false);
    onClose();
  };

  const handleRegenerate = async () => {
    setLoading(true);
    // Trigger re-scrape on backend
    try {
      const token = localStorage.getItem('session_token');
      const resp = await fetch(`${API_URL}/api/tools/${tool.id}/refresh`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setImageUrl(data.og_image_url || '');
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
        
        <h2>Change Image</h2>
        <p className="tool-name-small">{tool.name}</p>
        
        <div className="image-preview">
          {imageUrl ? (
            <img src={imageUrl} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
          ) : (
            <div className="no-image">No image</div>
          )}
        </div>
        
        <div className="form-group">
          <label>Image URL</label>
          <input 
            type="text" 
            value={imageUrl} 
            onChange={e => setImageUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>
        
        <div className="modal-actions">
          <button className="btn secondary" onClick={handleRegenerate} disabled={loading}>
            <i className="fas fa-sync"></i> Regenerate from URL
          </button>
          <button className="btn primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Delete Confirmation Modal
function DeleteModal({ tool, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);

  const handleDelete = async () => {
    setLoading(true);
    await onConfirm(tool.id);
    setLoading(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content delete-modal" onClick={e => e.stopPropagation()}>
        <h2>Delete Tool?</h2>
        <p>Are you sure you want to delete <strong>{tool.name}</strong>?</p>
        <p className="warning">This will remove it from your dashboard and Telegram.</p>
        
        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn danger" onClick={handleDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Settings Modal
function SettingsModal({ user, onClose, onSave }) {
  const [displayName, setDisplayName] = useState(user.display_name || '');
  const [isPublic, setIsPublic] = useState(user.is_public || false);
  const [publicSlug, setPublicSlug] = useState(user.public_slug || '');
  const [loading, setLoading] = useState(false);
  const [shareToken, setShareToken] = useState(user.share_token || '');
  const [copied, setCopied] = useState(null);

  const handleSave = async () => {
    setLoading(true);
    await onSave({ display_name: displayName, is_public: isPublic, public_slug: publicSlug });
    setLoading(false);
    onClose();
  };

  const handleGeneratePrivateLink = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    try {
      const resp = await fetch(`${API_URL}/api/auth/share-token`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        const data = await resp.json();
        setShareToken(data.share_token);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const publicUrl = isPublic && publicSlug ? `${window.location.origin}/u/${publicSlug}` : null;
  const privateUrl = shareToken ? `${window.location.origin}/s/${shareToken}` : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content settings-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        <div className="settings-header">
          <i className="fas fa-cog settings-header-icon"></i>
          <h2>Settings</h2>
        </div>

        {/* Profile Section */}
        <div className="settings-section">
          <h3 className="settings-section-title">
            <i className="fas fa-user"></i> Profile
          </h3>
          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
            />
          </div>
        </div>

        {/* Sharing Section */}
        <div className="settings-section">
          <h3 className="settings-section-title">
            <i className="fas fa-share-alt"></i> Sharing
          </h3>

          {/* Private Link */}
          <div className="share-option">
            <div className="share-option-header">
              <div>
                <span className="share-option-label">Private Link</span>
                <p className="hint">Only people with the link can view your dashboard</p>
              </div>
            </div>
            {privateUrl ? (
              <div className="share-url">
                <span>{privateUrl}</span>
                <button onClick={() => copyToClipboard(privateUrl, 'private')} title="Copy link">
                  <i className={copied === 'private' ? 'fas fa-check' : 'fas fa-copy'}></i>
                </button>
              </div>
            ) : (
              <button className="btn secondary btn-sm" onClick={handleGeneratePrivateLink}>
                <i className="fas fa-link"></i> Generate Private Link
              </button>
            )}
          </div>

          <div className="share-divider"></div>

          {/* Public Profile */}
          <div className="share-option">
            <div className="share-option-header">
              <div>
                <span className="share-option-label">Public Profile</span>
                <p className="hint">Anyone can find and view your dashboard</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={isPublic}
                  onChange={e => setIsPublic(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>

            {isPublic && (
              <>
                <div className="slug-input">
                  <span className="slug-prefix">{window.location.origin}/u/</span>
                  <input
                    type="text"
                    value={publicSlug}
                    onChange={e => setPublicSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="your-name"
                  />
                </div>
                {publicUrl && (
                  <div className="share-url">
                    <span>{publicUrl}</span>
                    <button onClick={() => copyToClipboard(publicUrl, 'public')} title="Copy link">
                      <i className={copied === 'public' ? 'fas fa-check' : 'fas fa-copy'}></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn secondary" onClick={onClose}>Cancel</button>
          <button className="btn primary" onClick={handleSave} disabled={loading}>
            {loading ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shared Profile View — used for both /u/{slug} (public) and /s/{token} (private)
function SharedProfile({ slug, shareToken }) {
  const [profile, setProfile] = useState(null);
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [githubLang, setGithubLang] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [total, setTotal] = useState(0);
  const [saved, setSaved] = useState(false);

  // Check if this dashboard is already saved
  useEffect(() => {
    const savedDashboards = JSON.parse(localStorage.getItem('saved_dashboards') || '[]');
    const key = slug || shareToken;
    setSaved(savedDashboards.some(d => d.slug === slug || d.token === shareToken));
  }, [slug, shareToken]);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);
      if (githubLang) params.append('github_language', githubLang);
      params.append('per_page', '100');

      const endpoint = shareToken
        ? `${API_URL}/api/public/shared/${shareToken}`
        : `${API_URL}/api/public/profile/${slug}`;
      const resp = await fetch(`${endpoint}?${params}`);
      if (resp.status === 404) {
        setError('Profile not found');
        setLoading(false);
        return;
      }
      if (resp.ok) {
        const data = await resp.json();
        setProfile(data.user);
        setTools(data.tools);
        setCategories(data.categories);
        setTotal(data.total);
      }
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, [slug, shareToken, activeCategory, searchQuery, githubLang]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const toggleSave = () => {
    const savedDashboards = JSON.parse(localStorage.getItem('saved_dashboards') || '[]');
    if (saved) {
      const filtered = savedDashboards.filter(d => !(d.slug === slug || d.token === shareToken));
      localStorage.setItem('saved_dashboards', JSON.stringify(filtered));
      setSaved(false);
    } else if (profile) {
      savedDashboards.push({
        slug: slug || null,
        token: shareToken || null,
        display_name: profile.display_name,
        avatar_url: profile.avatar_url,
        saved_at: new Date().toISOString(),
      });
      localStorage.setItem('saved_dashboards', JSON.stringify(savedDashboards));
      setSaved(true);
    }
  };

  // GitHub sub-categories: find unique languages in tools
  const githubTools = tools.filter(t => t.category === 'GitHub Repos');
  const githubLangs = [...new Set(githubTools.map(t => t.github_language).filter(Boolean))].sort();

  if (error) {
    return (
      <div className="app">
        <div className="empty-state" style={{ marginTop: '120px' }}>
          <i className="fas fa-user-slash"></i>
          <h3>{error}</h3>
          <p>This profile doesn't exist or isn't available.</p>
          <a href="/" className="btn primary">Go to Cool Tek</a>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="header-content">
          <div className="header-top">
            <a href="/" className="logo">
              <img src="/logo.png" alt="Cool Tek" className="logo-img" />
              <span>Cool Tek</span>
            </a>
            {profile && (
              <button className={`btn ${saved ? 'secondary' : 'primary'} btn-sm`} onClick={toggleSave}>
                <i className={saved ? 'fas fa-bookmark' : 'far fa-bookmark'}></i>
                {saved ? 'Saved' : 'Save Dashboard'}
              </button>
            )}
          </div>
          {profile && (
            <div className="public-profile-info">
              {profile.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="user-avatar" />
              ) : (
                <div className="user-avatar user-avatar-fallback">
                  <i className="fas fa-user"></i>
                </div>
              )}
              <div>
                <h2 className="profile-name">{profile.display_name}'s Dashboard</h2>
                <p className="tagline">{total} tool{total !== 1 ? 's' : ''} saved</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <div className="search-section">
        <div className="search-container">
          <i className="fas fa-search search-icon"></i>
          <input
            type="text"
            placeholder="Search tools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        <div className="results-count">{total} tool{total !== 1 ? 's' : ''}</div>
      </div>

      {categories.length > 0 && (
        <div className="category-filter">
          <button
            className={`category-btn ${!activeCategory ? 'active' : ''}`}
            onClick={() => { setActiveCategory(null); setGithubLang(null); }}
          >
            <i className="fas fa-th-large"></i> All
          </button>
          {categories.map((cat) => (
            <button
              key={cat.name}
              className={`category-btn ${activeCategory === cat.name && !githubLang ? 'active' : ''}`}
              onClick={() => { setActiveCategory(cat.name); setGithubLang(null); }}
            >
              <i className={getCategoryIcon(cat.name)}></i>
              {cat.name}
              <span className="category-count">{cat.count}</span>
            </button>
          ))}
        </div>
      )}

      {/* GitHub sub-categories */}
      {activeCategory === 'GitHub Repos' && githubLangs.length > 0 && (
        <div className="category-filter github-sub-filter">
          <button
            className={`category-btn sub ${!githubLang ? 'active' : ''}`}
            onClick={() => setGithubLang(null)}
          >
            All Languages
          </button>
          {githubLangs.map(lang => (
            <button
              key={lang}
              className={`category-btn sub ${githubLang === lang ? 'active' : ''}`}
              onClick={() => setGithubLang(lang)}
            >
              {lang}
              <span className="category-count">
                {githubTools.filter(t => t.github_language === lang).length}
              </span>
            </button>
          ))}
        </div>
      )}

      <main className="main-content">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : tools.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <h3>No tools yet</h3>
          </div>
        ) : (
          <div className="tools-grid">
            {(githubLang
              ? tools.filter(t => t.github_language === githubLang)
              : tools
            ).map((tool) => (
              <ToolCard key={tool.id} tool={tool} isOwner={false} onDelete={() => {}} onEditImage={() => {}} />
            ))}
          </div>
        )}
      </main>

      <footer className="footer">
        <p>
          <i className="fas fa-bolt"></i>
          Powered by <a href="/" style={{ color: 'var(--accent)' }}>Cool Tek</a> — Save any URL by messaging @cooltekbot on Telegram
        </p>
      </footer>
    </div>
  );
}

// Saved Dashboards View
function SavedDashboards({ onBack }) {
  const [dashboards, setDashboards] = useState([]);

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('saved_dashboards') || '[]');
    setDashboards(saved);
  }, []);

  const removeDashboard = (idx) => {
    const updated = dashboards.filter((_, i) => i !== idx);
    localStorage.setItem('saved_dashboards', JSON.stringify(updated));
    setDashboards(updated);
  };

  return (
    <div className="saved-dashboards">
      <div className="saved-dashboards-header">
        <button className="btn secondary btn-sm" onClick={onBack}>
          <i className="fas fa-arrow-left"></i> Back
        </button>
        <h2><i className="fas fa-bookmark"></i> Saved Dashboards</h2>
      </div>
      {dashboards.length === 0 ? (
        <div className="empty-state">
          <i className="far fa-bookmark"></i>
          <h3>No saved dashboards</h3>
          <p>When you visit someone's shared dashboard, you can save it here for easy access.</p>
        </div>
      ) : (
        <div className="saved-list">
          {dashboards.map((d, idx) => {
            const url = d.slug ? `/u/${d.slug}` : `/s/${d.token}`;
            return (
              <div key={idx} className="saved-item">
                <a href={url} className="saved-item-link">
                  {d.avatar_url ? (
                    <img src={d.avatar_url} alt="" className="saved-avatar" />
                  ) : (
                    <div className="saved-avatar saved-avatar-fallback">
                      <i className="fas fa-user"></i>
                    </div>
                  )}
                  <div className="saved-info">
                    <span className="saved-name">{d.display_name}</span>
                    <span className="saved-date">Saved {new Date(d.saved_at).toLocaleDateString()}</span>
                  </div>
                </a>
                <button className="saved-remove" onClick={() => removeDashboard(idx)} title="Remove">
                  <i className="fas fa-times"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Magic Login — exchanges code for JWT, stores it, redirects to dashboard
function MagicLogin({ code }) {
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/auth/magic/${code}`)
      .then(r => {
        if (!r.ok) throw new Error(r.status === 410 ? 'Login link has expired. Request a new one from the bot.' : 'Invalid or expired login link.');
        return r.json();
      })
      .then(data => {
        localStorage.setItem('session_token', data.access_token);
        window.location.replace('/');
      })
      .catch(err => setError(err.message));
  }, [code]);

  if (error) {
    return (
      <div className="app">
        <div className="empty-state">
          <i className="fas fa-exclamation-circle"></i>
          <h2>Login Failed</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="empty-state">
        <i className="fas fa-spinner fa-spin"></i>
        <h2>Logging you in...</h2>
      </div>
    </div>
  );
}

// Main App
function App() {
  // Check for /login/{code} magic login route
  const loginMatch = window.location.pathname.match(/^\/login\/([a-f0-9-]+)\/?$/);
  if (loginMatch) {
    return <MagicLogin code={loginMatch[1]} />;
  }

  // Check for /u/{slug} public profile route
  const pathMatch = window.location.pathname.match(/^\/u\/([a-z0-9-]+)\/?$/);
  if (pathMatch) {
    return <SharedProfile slug={pathMatch[1]} />;
  }

  // Check for /s/{token} private share route
  const tokenMatch = window.location.pathname.match(/^\/s\/([a-f0-9-]+)\/?$/);
  if (tokenMatch) {
    return <SharedProfile shareToken={tokenMatch[1]} />;
  }

  return <Dashboard />;
}

// Dashboard (main authenticated view)
function Dashboard() {
  const [user, setUser] = useState(null);
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState(null);
  const [githubLang, setGithubLang] = useState(null);
  const [total, setTotal] = useState(0);
  const [showSaved, setShowSaved] = useState(false);

  // Modals
  const [showLogin, setShowLogin] = useState(false);
  const [editingTool, setEditingTool] = useState(null);
  const [deletingTool, setDeletingTool] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  // Check for auth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionToken = params.get('session_token');
    if (sessionToken) {
      localStorage.setItem('session_token', sessionToken);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Check auth on load
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (token) {
      fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (data) setUser(data);
          else localStorage.removeItem('session_token');
        })
        .catch(() => localStorage.removeItem('session_token'));
    }
  }, []);

  const fetchTools = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      // Not logged in — show no tools
      setTools([]);
      setTotal(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeCategory) params.append('category', activeCategory);
      if (searchQuery) params.append('search', searchQuery);
      params.append('per_page', '100');

      const headers = { 'Authorization': `Bearer ${token}` };
      const response = await fetch(`${API_URL}/api/tools?${params}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setTools(data.tools);
        setTotal(data.total);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, searchQuery]);

  const fetchCategories = useCallback(async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setCategories([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/tools/categories`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, user]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools, user]);

  const handleLogout = async () => {
    const token = localStorage.getItem('session_token');
    if (token) {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
    localStorage.removeItem('session_token');
    setUser(null);
    fetchTools();
  };

  const handleDeleteTool = async (toolId) => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const resp = await fetch(`${API_URL}/api/tools/${toolId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (resp.ok) {
        setTools(tools.filter(t => t.id !== toolId));
        setTotal(total - 1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateImage = async (toolId, imageUrl) => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const resp = await fetch(`${API_URL}/api/tools/${toolId}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ custom_image_url: imageUrl })
      });
      if (resp.ok) {
        const updated = await resp.json();
        setTools(tools.map(t => t.id === toolId ? { ...t, ...updated } : t));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateSettings = async (settings) => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    
    try {
      const resp = await fetch(`${API_URL}/api/auth/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (resp.ok) {
        const updated = await resp.json();
        setUser(updated);
      }
    } catch (err) {
      console.error(err);
    }
  };

  if (showSaved) {
    return (
      <AuthContext.Provider value={{ user, setUser }}>
        <div className="app" data-testid="app-container">
          <header className="header">
            <div className="header-content">
              <div className="header-top">
                <div className="logo">
                  <img src="/logo.png" alt="Cool Tek" className="logo-img" />
                  <span>Cool Tek</span>
                </div>
              </div>
            </div>
          </header>
          <main className="main-content" style={{ paddingTop: '2rem' }}>
            <SavedDashboards onBack={() => setShowSaved(false)} />
          </main>
        </div>
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <div className="app" data-testid="app-container">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            <div className="header-top">
              <div className="logo">
                <img src="/logo.png" alt="Cool Tek" className="logo-img" />
                <span>Cool Tek</span>
              </div>
              <div className="header-actions">
                {user ? (
                  <div className="user-menu">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="user-avatar" />
                    ) : (
                      <div className="user-avatar user-avatar-fallback">
                        <i className="fas fa-user"></i>
                      </div>
                    )}
                    <span className="user-name">{user.display_name}</span>
                    <div className="user-dropdown">
                      <button onClick={() => setShowSettings(true)}>
                        <i className="fas fa-cog"></i> Settings
                      </button>
                      <button onClick={() => setShowSaved(true)}>
                        <i className="fas fa-bookmark"></i> Saved Dashboards
                      </button>
                      {user.is_public && (
                        <a href={`/u/${user.public_slug}`} target="_blank" rel="noopener noreferrer">
                          <i className="fas fa-share"></i> View Public Profile
                        </a>
                      )}
                      <button onClick={handleLogout}>
                        <i className="fas fa-sign-out-alt"></i> Logout
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="login-header-btn" onClick={() => setShowLogin(true)}>
                    <i className="fas fa-user"></i> Login
                  </button>
                )}
              </div>
            </div>
            <p className="tagline">{user ? 'Your saved AI tools' : 'AI Tools & Links saved via Telegram'}</p>
            <a href="https://t.me/cooltekbot" target="_blank" rel="noopener noreferrer" className="telegram-btn">
              <i className="fab fa-telegram-plane"></i>
              {user ? 'Add tools via @cooltekbot' : 'Start Saving with @cooltekbot'}
            </a>
          </div>
        </header>

        {/* Search */}
        <div className="search-section">
          <div className="search-container">
            <i className="fas fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              data-testid="search-input"
            />
            {searchQuery && (
              <button className="clear-search" onClick={() => setSearchQuery('')}>
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
          <div className="results-count">
            {total} tool{total !== 1 ? 's' : ''} {user ? 'saved' : 'found'}
          </div>
        </div>

        {/* Categories */}
        {categories.length > 0 && (
          <div className="category-filter" data-testid="category-filter">
            <button
              className={`category-btn ${!activeCategory ? 'active' : ''}`}
              onClick={() => { setActiveCategory(null); setGithubLang(null); }}
            >
              <i className="fas fa-th-large"></i> All
            </button>
            {categories.map((cat) => (
              <button
                key={cat.name}
                className={`category-btn ${activeCategory === cat.name && !githubLang ? 'active' : ''}`}
                onClick={() => { setActiveCategory(cat.name); setGithubLang(null); }}
              >
                <i className={getCategoryIcon(cat.name)}></i>
                {cat.name}
                <span className="category-count">{cat.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* GitHub language sub-filter */}
        {activeCategory === 'GitHub Repos' && (() => {
          const ghTools = tools.filter(t => t.category === 'GitHub Repos');
          const langs = [...new Set(ghTools.map(t => t.github_language).filter(Boolean))].sort();
          if (langs.length === 0) return null;
          return (
            <div className="category-filter github-sub-filter">
              <button
                className={`category-btn sub ${!githubLang ? 'active' : ''}`}
                onClick={() => setGithubLang(null)}
              >
                All Languages
              </button>
              {langs.map(lang => (
                <button
                  key={lang}
                  className={`category-btn sub ${githubLang === lang ? 'active' : ''}`}
                  onClick={() => setGithubLang(lang)}
                >
                  {lang}
                  <span className="category-count">
                    {ghTools.filter(t => t.github_language === lang).length}
                  </span>
                </button>
              ))}
            </div>
          );
        })()}

        {/* Main Content */}
        <main className="main-content">
          {loading ? (
            <div className="loading" data-testid="loading">
              <div className="spinner"></div>
              <p>Loading tools...</p>
            </div>
          ) : tools.length === 0 ? (
            <div className="empty-state" data-testid="empty-state">
              <i className="fas fa-inbox"></i>
              <h3>No tools yet</h3>
              <p>{user ? 'Start saving AI tools by messaging @cooltekbot on Telegram!' : 'Login to see your saved tools'}</p>
              {!user && (
                <button className="btn primary btn-sm" onClick={() => setShowLogin(true)}>
                  <i className="fas fa-sign-in-alt"></i> Login
                </button>
              )}
            </div>
          ) : (
            <div className="tools-grid" data-testid="tools-grid">
              {(githubLang
                ? tools.filter(t => t.github_language === githubLang)
                : tools
              ).map((tool) => (
                <ToolCard
                  key={tool.id}
                  tool={tool}
                  isOwner={!!user}
                  onDelete={setDeletingTool}
                  onEditImage={setEditingTool}
                />
              ))}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="footer">
          <p>
            <i className="fas fa-bolt"></i>
            Powered by Cool Tek Bot — Save any URL by messaging @cooltekbot on Telegram
          </p>
        </footer>

        {/* Modals */}
        {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}
        {editingTool && (
          <EditImageModal 
            tool={editingTool} 
            onClose={() => setEditingTool(null)}
            onSave={handleUpdateImage}
          />
        )}
        {deletingTool && (
          <DeleteModal 
            tool={deletingTool} 
            onClose={() => setDeletingTool(null)}
            onConfirm={handleDeleteTool}
          />
        )}
        {showSettings && user && (
          <SettingsModal 
            user={user} 
            onClose={() => setShowSettings(false)}
            onSave={handleUpdateSettings}
          />
        )}
      </div>
    </AuthContext.Provider>
  );
}

export default App;
